import React, { useState, useEffect } from "react";
import { planApi } from "../../services/api"; // <-- adjust path if needed
import { Button, Stack, Typography, Box, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from "@mui/material";
import { ValidationSample } from "../../types";
import { useNavigate } from 'react-router-dom';
import {
  PageHeader,
} from '../../components/SharedPageParts';
import { DocumentScannerOutlined } from "@mui/icons-material";

export default function PlanUpload() {
  // -----------------------------
  // Upload State
  // -----------------------------
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [progress, setProgress] = useState(0);
  const theme = useTheme();
  const navigate = useNavigate();


  // -----------------------------
  // Dashboard State
  // -----------------------------
  const [plans, setPlans] = useState<any[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // -----------------------------
  // Status History Modal State
  // -----------------------------
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [statusHistory, setStatusHistory] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);

  // -----------------------------
  // File Selection
  // -----------------------------
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] || null);
    setStatus(null);
    setError(null);
  };

  // -----------------------------
  // Upload Handler
  // -----------------------------
  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(10);
    setError(null);
    setStatus("Uploading file...");

    try {
      // Phase 1 — Uploading to server
      setProgress(30);
      setStatus("Uploading file to server...");

      // Smooth animation during backend processing (Phase 2)
      let p = 30;
      const interval = setInterval(() => {
        p += 5;
        setProgress(p);
        if (p >= 70) clearInterval(interval);
      }, 300);

      // Call backend

      const json = await planApi.uploadPlan(file);

      clearInterval(interval);

      // -----------------------------
      // ERROR HANDLING (both types)
      // -----------------------------
      if (json.errors) {
        const err = json.errors[0];

        // Case 1: Excel validation error (has count + sample)
        if (err.count !== undefined && err.sample !== undefined) {
          const total = err.count;
          const sample = err.sample;

          let message = `${err.title}. ${total} issues found in the file.\n\nExample issues:\n`;

          (sample as ValidationSample[]).forEach((e) => {
            message += `• Row ${e.row}, Column ${e.column}: ${e.message}\n`;
          });

          setError(message);
          setStatus(null);
          setProgress(0);
          setUploading(false);
          return;
        }

        // Case 2: Internal server error (no count/sample)
        const detail = err.detail || "Unexpected server error.";
        setError(`${err.title}: ${detail}`);
        setStatus(null);
        setProgress(0);
        setUploading(false);
        return;
      }

      // -----------------------------
      // SUCCESS
      // -----------------------------
      const planId = json.data;

      setProgress(90);
      setStatus("Processing uploaded file...");

      setProgress(100);
      setStatus(`Upload completed successfully. Plan ID: ${planId}`);

      await loadPlans();
    } catch (err) {
      // Network or unexpected JS error
      console.error(err);
      setError("Upload failed — server error.");
      setStatus(null);
      setProgress(0);
    }

    setUploading(false);
  };

  const FileUploadButton = () => (
  <>
    <input
      id="plan-file-input"
      type="file"
      accept=".xlsx"
      style={{ display: "none" }}
      onChange={handleFileChange}
    />

    <label htmlFor="plan-file-input">
      <Button
        variant="contained"
        component="span"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
          color: 'white',
          fontWeight: 600,
          '&:hover': {
            background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)'
          }
        }}
      >
        Choose File
      </Button>
    </label>
  </>
);

  // -----------------------------
  // Load Plans for Dashboard
  // -----------------------------
  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const list = await planApi.listPlans();
      console.log("Plans loaded:", list);
      setPlans(list);
    } catch (err) {
      console.error("Failed to load plans", err);
    }
    setLoadingPlans(false);
  };

  useEffect(() => {
    loadPlans();
  }, []);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    // const interval = setInterval(loadPlans, 60000);
    // return () => clearInterval(interval);
    loadPlans();
  }, []);

  // -----------------------------
  // Load Status History for Modal
  // -----------------------------
  const loadStatusHistory = async (planId: number) => {
    try {
      const history = await planApi.getPlanStatusHistory(planId);
      setStatusHistory(history);
      setShowModal(true);
    } catch (err) {
      console.error("Failed to load status history", err);
    }
  };

  // -----------------------------
  // Status Badge Component
  // -----------------------------
  const getColor = (line: string) => {
    if (line.includes("Completed")) return "green";
    if (line.includes("Failed")) return "red"
    return "orange";
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const lines = status.split("\n");

    return (
      <div>
        {lines.map((line, idx) => (
          <div key={idx} style={{ color: getColor(line) }}>
            {line}
          </div>
        ))}
      </div>
    );
  };


  // -----------------------------
  // Render UI
  // -----------------------------
  return (
    
    <div style={{ paddingLeft: 10 }}>
      <PageHeader
                                                          title="Production Plan Upload"
                                                          Icon={DocumentScannerOutlined}
                                                          onBack={() => navigate('/')}
                                                          backLabel="Back to Dashboard"
                                                          subtitle=""
                                                        />

      {/* Upload Section */}
        <Stack direction="row" spacing={3} alignItems="center" marginBottom={3}>
        
        {/* Choose File */}
        <FileUploadButton />

        {/* Show selected filename */}
        {file && (
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            Selected File: {file.name}
          </Typography>
        )}

        {/* Upload Button */}
        <Button
            variant="contained"
            color="success"
            disabled={!file || uploading}
            onClick={handleUpload}
            sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                color: 'white',
              }}
        >
            {uploading ? "Uploading..." : "Upload"}
        </Button>

        {/* Download Template */}
        <Button
            variant="outlined"
            sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                color: 'white'
              }}
            onClick={() => planApi.downloadTemplate()}
        >
            Download Template
        </Button>
        </Stack>

        {/* Status Messages */}
        {status && (
        <div style={{ marginTop: 10, color: "green" }}>
            {status}
        </div>
        )}

        {error && (
        <div style={{ marginTop: 10, color: "red" }}>
            {error}
        </div>
        )}
      
      {uploading && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}


      {/* Dashboard Section */}
      <h3>Uploaded Plans</h3>

      {loadingPlans && <div>Loading plans...</div>}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          backgroundColor: theme.palette.background.default,
          borderColor: theme.palette.divider,
          mt: 2
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.background.paper }}>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Plan ID</TableCell>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Horizon</TableCell>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Unique ID</TableCell>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Plan Start Date</TableCell>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Plan End Date</TableCell>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Uploaded On</TableCell>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {plans.map((p) => (
              <TableRow
                key={p.planId}
                hover
                sx={{
                  "&:hover": { backgroundColor: theme.palette.background.paper },
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}
              >
                <TableCell sx={{ borderBottom: "none" }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                    {p.planId}
                  </Typography>
                </TableCell>

                <TableCell sx={{ borderBottom: "none" }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                    {p.planHorizon}
                  </Typography>
                </TableCell>

                <TableCell sx={{ borderBottom: "none" }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                    {p.planUniqueId}
                  </Typography>
                </TableCell>

                <TableCell sx={{ borderBottom: "none" }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                    {p.planStartDate}
                  </Typography>
                </TableCell>

                <TableCell sx={{ borderBottom: "none" }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                    {p.planEndDate}
                  </Typography>
                </TableCell>

                <TableCell sx={{ borderBottom: "none" }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                    {new Date(p.createdAt + "Z").toLocaleString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </Typography>
                </TableCell>

                <TableCell sx={{ borderBottom: "none" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {p.status?.map((entry: any, index: number) => {
                      const localTime = new Date(entry.created_at).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      });

                      return (
                        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                            {localTime}
                          </Typography>
                          <StatusBadge status={entry.status} />
                        </Box>
                      );
                    })}
                  </Box>
                </TableCell>


              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

